import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/pathways";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";

const ACTION_COLOR = { CANCEL: "#E63946", REPLACE: "#D4AF37", BUNDLE: "#7aa2f7", KEEP: "#5FB878" };

export default function Audit() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [mode, setMode] = useState(params.get("type") === "subscriptions" ? "subscriptions" : "menu");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      <div className="eyebrow mb-4">THE CHEAPER WAY FLOW</div>
      <h1 className="font-display text-4xl md:text-6xl font-bold leading-[0.95] mb-10">
        {mode === "subscriptions" ? "YOUR SUBSCRIPTION AUDIT" : "WHAT KIND OF AUDIT?"}
      </h1>

      {mode === "menu" && (
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            ["ONE PURCHASE", "Run a single thing you're about to buy through the audit.", () => nav("/find")],
            ["MY SUBSCRIPTIONS", "Detect overlap and waste across your monthly spend.", () => setMode("subscriptions")],
            ["QUICK AUDIT", "Category, item, price — instant recommendations.", () => setMode("quick")],
            ["MY UPCOMING TRIP", "Flights, hotels, activities — the cheaper way.", () => nav("/find?q=" + encodeURIComponent("I'm planning a trip"))],
          ].map(([t, d, fn]) => (
            <button key={t} onClick={fn} data-testid={`audit-menu-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group text-left border hairline bg-[#141417] p-7 hover:border-[#E63946] transition-colors">
              <h3 className="font-display text-2xl font-semibold mb-2">{t}</h3>
              <p className="text-sm text-stone-400">{d}</p>
            </button>
          ))}
        </div>
      )}

      {mode === "quick" && <QuickAudit onBack={() => setMode("menu")} />}
      {mode === "subscriptions" && <SubscriptionAudit onBack={() => setMode("menu")} />}
    </div>
  );
}

function QuickAudit({ onBack }) {
  const nav = useNavigate();
  const [f, setF] = useState({ category: "Technology", item: "", price: "", billing: "one-time", location: "" });
  const submit = (e) => {
    e.preventDefault();
    if (!f.item.trim()) { toast.error("Tell us what it is."); return; }
    const q = `I need ${f.item} (${f.category})`;
    const p = new URLSearchParams({ q });
    nav(`/find?${p.toString()}`);
  };
  return (
    <form onSubmit={submit} className="max-w-2xl space-y-4">
      <button type="button" onClick={onBack} className="font-label text-[10px] tracking-widest text-stone-500 mb-2">← BACK</button>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="CATEGORY">
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="quick-category"
            className="w-full bg-[#141417] border hairline px-4 py-3 font-label text-sm outline-none focus:border-[#E63946]">
            {["Home","Transportation","Food","Beauty","Technology","Health","Travel","Entertainment","Work","Money"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="ITEM / SERVICE">
          <input value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} placeholder="e.g. a laptop" data-testid="quick-item"
            className="w-full bg-transparent border hairline px-4 py-3 font-label text-sm outline-none focus:border-[#E63946]" />
        </Field>
      </div>
      <button type="submit" data-testid="quick-submit"
        className="font-label text-xs tracking-widest bg-[#E63946] text-white px-8 py-4 hover:bg-[#c62d39] transition-colors">
        FIND THE CHEAPER WAY →
      </button>
    </form>
  );
}

function SubscriptionAudit({ onBack }) {
  const [items, setItems] = useState([
    { name: "Netflix", price: "22" }, { name: "Canva", price: "15" },
    { name: "Adobe", price: "60" }, { name: "Notion", price: "10" }, { name: "ChatGPT", price: "20" },
  ]);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const total = items.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);
  const set = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => setItems([...items, { name: "", price: "" }]);
  const del = (i) => setItems(items.filter((_, idx) => idx !== i));

  const run = async () => {
    const clean = items.filter((i) => i.name.trim() && i.price).map((i) => ({ name: i.name, price: parseFloat(i.price) }));
    if (!clean.length) { toast.error("Add at least one subscription."); return; }
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/subscription-audit", { items: clean });
      setRes(data.result);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <button type="button" onClick={onBack} className="font-label text-[10px] tracking-widest text-stone-500">← BACK</button>
      <div className="max-w-2xl border hairline bg-[#141417]">
        <div className="px-6 py-4 border-b hairline flex justify-between items-baseline">
          <span className="eyebrow">YOUR MONTHLY STACK</span>
          <span className="font-display text-3xl text-[#E63946]">{money(total)}<span className="text-sm text-stone-500">/mo</span></span>
        </div>
        <div className="p-6 space-y-3">
          {items.map((it, i) => (
            <div key={i} className="flex gap-3 items-center">
              <input value={it.name} onChange={(e) => set(i, "name", e.target.value)} placeholder="Service" data-testid={`sub-name-${i}`}
                className="flex-1 bg-transparent border hairline px-4 py-3 font-label text-sm outline-none focus:border-[#E63946]" />
              <div className="flex items-center border hairline">
                <span className="pl-3 text-stone-500 font-label text-sm">$</span>
                <input value={it.price} onChange={(e) => set(i, "price", e.target.value)} type="number" placeholder="0" data-testid={`sub-price-${i}`}
                  className="w-24 bg-transparent px-2 py-3 font-label text-sm outline-none" />
              </div>
              <button onClick={() => del(i)} data-testid={`sub-del-${i}`} className="text-stone-600 hover:text-[#E63946] p-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={add} data-testid="sub-add" className="flex items-center gap-2 font-label text-[10px] tracking-widest text-stone-400 hover:text-[#E63946] pt-2">
            <Plus className="w-3 h-3" /> ADD ANOTHER
          </button>
        </div>
        <div className="px-6 py-4 border-t hairline">
          <button onClick={run} disabled={loading} data-testid="sub-audit-btn"
            className="font-label text-xs tracking-widest bg-[#E63946] text-white px-8 py-4 hover:bg-[#c62d39] transition-colors disabled:opacity-50 flex items-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} {loading ? "AUDITING…" : "AUDIT MY SUBSCRIPTIONS →"}
          </button>
        </div>
      </div>

      {res && (
        <div className="space-y-8">
          <div className="border-2 border-[#E63946] bg-[#141417] p-8">
            <div className="eyebrow mb-2">THE AUDIT</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold">YOU'RE PAYING {money(res.total_monthly)}/MONTH.</h2>
            <p className="font-display text-2xl text-stone-200 mt-4 redpen-underline inline">{res.verdict}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 border-l border-t hairline" data-testid="sub-summary">
            {[["CANCEL", res.summary?.cancel, "#E63946"],["REPLACE", res.summary?.replace, "#D4AF37"],["BUNDLE", res.summary?.bundle, "#7aa2f7"],["KEEP", res.summary?.keep, "#5FB878"],["SAVE/MO", res.summary?.potential_monthly_savings, "#F5F2EB"]].map(([l, v, c]) => (
              <div key={l} className="border-r border-b hairline p-5">
                <div className="font-label text-[10px] tracking-widest text-stone-500 mb-1">{l}</div>
                <div className="font-display text-2xl" style={{ color: c }}>{money(v)}</div>
              </div>
            ))}
          </div>

          {res.overlaps?.length > 0 && (
            <div>
              <h3 className="font-label text-sm tracking-widest text-[#E63946] mb-4">DUPLICATE DETECTION</h3>
              <div className="space-y-3">
                {res.overlaps.map((o, i) => (
                  <div key={i} data-testid={`overlap-${i}`} className="border hairline bg-[#141417] p-5">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <span className="font-label text-xs tracking-widest text-[#D4AF37] border border-[#D4AF37] px-2 py-1">{o.label}</span>
                      <span className="font-label text-xs text-stone-400">{o.services?.join(" + ")}</span>
                    </div>
                    <p className="text-sm text-stone-400">{o.note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="font-label text-sm tracking-widest text-[#E63946] mb-4">LINE BY LINE</h3>
            <div className="border-t hairline">
              {res.items?.map((it, i) => (
                <div key={i} data-testid={`sub-item-${i}`} className="border-b hairline py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="sm:w-40 font-display text-xl">{it.name}</div>
                  <div className="sm:w-24 font-label text-sm text-stone-400">{money(it.price)}/mo</div>
                  <div className="sm:w-28"><span className="font-label text-xs tracking-widest px-2 py-1 border" style={{ color: ACTION_COLOR[it.action] || "#F5F2EB", borderColor: ACTION_COLOR[it.action] || "#F5F2EB" }}>{it.action}</span></div>
                  <div className="flex-1 text-sm text-stone-400">{it.note}{it.replacement ? <span className="text-[#D4AF37]"> → {it.replacement}</span> : null}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 font-display text-xl text-[#5FB878]">Potential annual difference: {money(res.summary?.potential_annual_savings)}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-label text-[10px] tracking-widest text-stone-500 block mb-2">{label}</span>
      {children}
    </label>
  );
}
